import {
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    Image,
    View
} from "react-native";
import type ProfileDetailModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Sticker,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    ProfileRequestDetailPayloadType
} from "../../../../services/restAPI/actions/getProfileUpdateRequest/type";
import {
    CheckIcon,
    XIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";

const ProfileDetailModal = ({
    historyItem = null,
    isHistory = false,
    isVisible,
    onApprove,
    requestID,
    onReject,
    onClose
}: ProfileDetailModalProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        detail,
        setDetail
    ] = useState<ProfileRequestDetailPayloadType | null>(null);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    useEffect(() => {
        if (!isVisible) {
            setDetail(null);
            return;
        }

        if (historyItem) {
            setDetail({
                requestedChanges: {
                    profilePhotoPath: historyItem.profilePhotoPath,
                    aboutMyTrade: historyItem.aboutMyTrade,
                    phoneNumber: historyItem.phoneNumber,
                    companies: historyItem.companies,
                    firstName: historyItem.firstName,
                    lastName: historyItem.lastName,
                    mail: historyItem.mail
                },
                createdAt: historyItem.createdAt,
                requestID: historyItem.historyID,
                userID: historyItem.userID
            });
            setLoading(false);
            return;
        }

        if (!requestID) {
            setDetail(null);
            return;
        }

        const fetchDetail = async () => {
            try {
                setLoading(true);
                const response = await RESTServiceContext.action("getProfileUpdateRequest", {
                    requestID
                });

                if (response.payload) {
                    setDetail(response.payload);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [
        historyItem,
        requestID,
        isVisible
    ]);

    const renderCurrentDiffValue = (isPhoto?: boolean, currentValue?: string) => {
        if (isPhoto) {
            if (!currentValue) {
                return <Text
                    customColor={colors.content.text.low}
                    variant="bodySmallSize"
                >
                    -
                </Text>;
            }

            return <Image
                style={[
                    stylesheet.profilePhotoPreview,
                    {
                        borderColor: colors.content.border.subtle,
                        borderRadius: radiuses.full,
                        borderWidth: borders.line
                    }
                ]}
                source={{
                    uri: currentValue
                }}
            />;
        }

        return <Text
            customColor={colors.content.text.mid}
            variant="bodyMediumSize"
        >
            {currentValue || "-"}
        </Text>;
    };

    const renderRequestedDiffValue = (isPhoto?: boolean, requestedValue?: string, isChanged?: boolean) => {
        if (isPhoto) {
            if (!requestedValue) {
                return <Text
                    customColor={colors.content.text.low}
                    variant="bodySmallSize"
                >
                    -
                </Text>;
            }

            return <View
                style={[
                    stylesheet.photoDiffContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Image
                    style={[
                        stylesheet.profilePhotoPreview,
                        {
                            borderColor: isChanged ? colors.system.default.success : colors.content.border.subtle,
                            borderRadius: radiuses.full,
                            borderWidth: borders.line
                        }
                    ]}
                    source={{
                        uri: requestedValue
                    }}
                />
                {
                    isChanged ?
                        <View
                            style={stylesheet.badgeContainer}
                        >
                            <Sticker
                                title={localize("requests-diff-changed")}
                                spreadBehaviour="free"
                                type="success"
                                size="small"
                            />
                        </View>
                    :
                        null
                }
            </View>;
        }

        return <View
            style={[
                stylesheet.requestedValueWrapper,
                {
                    gap: spaces.spacingXs
                }
            ]}
        >
            <Text
                customColor={colors.content.text.high}
                variant="bodyMediumSize"
            >
                {requestedValue || "-"}
            </Text>
            {
                isChanged ?
                    <View
                        style={stylesheet.badgeContainer}
                    >
                        <Sticker
                            title={localize("requests-diff-changed")}
                            spreadBehaviour="free"
                            type="success"
                            size="small"
                        />
                    </View>
                :
                    null
            }
        </View>;
    };

    const renderDiffRow = (
        label: string,
        currentValue?: string,
        requestedValue?: string,
        isPhoto: boolean = false
    ) => {
        const isChanged = requestedValue !== undefined && requestedValue !== currentValue;

        return <View
            style={[
                stylesheet.diffRow,
                {
                    borderColor: colors.content.border.subtle,
                    borderBottomWidth: borders.seperator,
                    paddingHorizontal: spaces.spacingMd,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            key={label}
        >
            <View
                style={stylesheet.fieldLabelContainer}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="labelMediumSize"
                >
                    {label}
                </Text>
            </View>
            <View
                style={stylesheet.currentValueContainer}
            >
                {renderCurrentDiffValue(isPhoto, currentValue)}
            </View>
            <View
                style={stylesheet.requestedValueContainer}
            >
                {renderRequestedDiffValue(isPhoto, requestedValue, isChanged)}
            </View>
        </View>;
    };

    const renderContent = () => {
        if (loading) {
            return <View
                style={stylesheet.loadingContainer}
            >
                <ActivityIndicator
                    color={colors.content.text.high}
                    size="large"
                />
            </View>;
        }

        if (!detail) {
            return <View
                style={[
                    stylesheet.errorContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("notifications-detail-load-error")}
                </Text>
            </View>;
        }

        const current = detail.currentProfile || {};
        const requested = detail.requestedChanges || {};

        const currentName = `${current.firstName || ""} ${current.lastName || ""}`.trim();
        const requestedName = `${requested.firstName || ""} ${requested.lastName || ""}`.trim();

        return <ScrollView
            style={stylesheet.contentContainer}
            showsVerticalScrollIndicator={true}
        >
            <View
                style={stylesheet.diffTableContainer}
            >
                <View
                    style={[
                        stylesheet.tableHeaderRow,
                        {
                            backgroundColor: colors.content.container.subtle,
                            paddingHorizontal: spaces.spacingMd,
                            paddingVertical: spaces.spacingSm,
                            borderRadius: radiuses.sm,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.fieldLabelContainer}
                    >
                        <Text
                            customColor={colors.content.text.low}
                            variant="labelSmallSize"
                        >
                            {localize("requests-diff-field")}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.currentValueContainer}
                    >
                        <Text
                            customColor={colors.content.text.low}
                            variant="labelSmallSize"
                        >
                            {localize("requests-diff-current")}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.requestedValueContainer}
                    >
                        <Text
                            customColor={colors.content.text.low}
                            variant="labelSmallSize"
                        >
                            {localize("requests-diff-requested")}
                        </Text>
                    </View>
                </View>
                {renderDiffRow(localize("requests-field-name"), currentName, requestedName)}
                {renderDiffRow(localize("requests-field-mail"), current.mail, requested.mail)}
                {renderDiffRow(localize("requests-field-phone"), current.phoneNumber, requested.phoneNumber)}
                {renderDiffRow(localize("requests-field-trade"), current.aboutMyTrade, requested.aboutMyTrade)}
                {renderDiffRow(localize("requests-field-photo"), current.profilePhotoPath, requested.profilePhotoPath, true)}
            </View>
        </ScrollView>;
    };

    return <Modal
        id="profile-detail-modal"
        portalName="modal-system"
        isActive={isVisible}
        onClosed={onClose}
    >
        <View
            style={[
                stylesheet.container,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingXl,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={stylesheet.headerContainer}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleMediumSize"
                >
                    {localize("requests-detail-profile-title")}
                </Text>
                <TouchableOpacity
                    style={stylesheet.closeButton}
                    onPress={onClose}
                >
                    <XIcon
                        color={colors.content.icon.mid}
                        size={24}
                    />
                </TouchableOpacity>
            </View>
            {renderContent()}
            <View
                style={[
                    stylesheet.footerContainer,
                    {
                        justifyContent: !isHistory && (onReject || onApprove) ? "space-between" : "flex-end"
                    }
                ]}
            >
                <Button
                    title={localize("notifications-detail-close")}
                    customTextColor={colors.project.brandWhite}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    onPress={onClose}
                    variant="filled"
                />
                {
                    !isHistory && (onReject || onApprove) ?
                        <View
                            style={[
                                stylesheet.footerActionsContainer,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            {
                                onReject ?
                                    <Button
                                        onPress={() => requestID && onReject(requestID)}
                                        title={localize("requests-action-reject")}
                                        icon={({
                                            color,
                                            size
                                        }) => <XIcon
                                            color={colors.content.icon[color]}
                                            size={size}
                                        />}
                                        spreadBehaviour="free"
                                        variant="filled"
                                        type="danger"
                                    />
                                :
                                    null
                            }
                            {
                                onApprove ?
                                    <Button
                                        onPress={() => requestID && onApprove(requestID)}
                                        title={localize("requests-action-approve")}
                                        icon={({
                                            color,
                                            size
                                        }) => <CheckIcon
                                            color={colors.content.icon[color]}
                                            size={size}
                                        />}
                                        spreadBehaviour="free"
                                        variant="filled"
                                        type="success"
                                    />
                                :
                                    null
                            }
                        </View>
                    :
                        null
                }
            </View>
        </View>
    </Modal>;
};
export default ProfileDetailModal;
